import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import CanonicalTag from "@/components/CanonicalTag";

const MediterraneanADUDesignRedwoodCity = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Mediterranean ADU Design & Construction in Redwood City",
    "description": "Create stunning Mediterranean ADUs in Redwood City with stucco walls, tile roofs, and elegant coastal-inspired architecture.",
    "url": "https://www.udaadu.com/mediterranean-adu-design-redwood-city",
    "mainEntity": {
      "@type": "Service",
      "name": "Mediterranean ADU Design & Construction",
      "provider": {
        "@type": "Organization",
        "name": "UDA ADU"
      },
      "areaServed": "Redwood City, CA"
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/mediterranean-adu-design-redwood-city" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <Navigation />

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button onClick={closeCalendlyModal} className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors">
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" width="100%" height="100%" frameBorder="0" title="Schedule a meeting" />
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">
            Mediterranean ADU Design & Construction in Redwood City
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Create stunning Mediterranean ADUs in Redwood City with stucco walls, tile roofs, and elegant coastal-inspired architecture.
          </p>
        </div>

        <div className="text-center mb-16">
          <Button onClick={openCalendlyModal} size="lg" className="mr-4">
            Get Free Consultation
          </Button>
          <Button asChild variant="outline" size="lg" className="text-white border-white hover:bg-white hover:text-black">
            <Link to="/adu-calculator">Calculate Costs</Link>
          </Button>
        </div>

        {/* Why Choose Mediterranean */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-white">Why Choose Mediterranean ADUs in Redwood City?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-white">Coastal Elegance</h3>
              <p className="text-muted-foreground">
                Mediterranean ADUs bring sophisticated coastal charm to Redwood City with warm stucco walls and red tile roofs.
              </p>
            </div>
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-white">Climate Perfect</h3>
              <p className="text-muted-foreground">
                Designed for Mediterranean climates, these ADUs naturally suit Redwood City's mild weather with outdoor living spaces.
              </p>
            </div>
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-white">Timeless Value</h3>
              <p className="text-muted-foreground">
                Mediterranean architecture maintains its appeal and value, making these ADUs excellent long-term investments in Redwood City.
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-white">Mediterranean ADU Features</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-2xl font-semibold mb-4 text-white">Exterior Elements</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li>• Stucco walls in warm earth tones</li>
                <li>• Red clay tile roofing</li>
                <li>• Arched windows and doorways</li>
                <li>• Wrought iron balconies</li>
                <li>• Private courtyards and patios</li>
              </ul>
            </div>
            <div>
              <h3 className="text-2xl font-semibold mb-4 text-white">Interior Design</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li>• Tile and natural stone flooring</li>
                <li>• Exposed wooden beam ceilings</li>
                <li>• Arched openings and niches</li>
                <li>• Warm color palettes</li>
                <li>• Built-in storage and seating</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Cost Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-white">Investment Range</h2>
          <div className="text-center p-8 border border-border rounded-lg">
            <p className="text-2xl font-bold mb-4 text-white">$270,000 - $430,000</p>
            <p className="text-muted-foreground">
              Mediterranean ADUs offer excellent value with authentic materials and architectural details that enhance Redwood City properties.
            </p>
          </div>
        </section>

        {/* Final CTA */}
        <section className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-8 text-white">Start Your Mediterranean ADU Project</h2>
          <p className="text-xl mb-8 text-muted-foreground max-w-3xl mx-auto">
            Ready to bring Mediterranean elegance to your Redwood City property? Let's create your perfect coastal-inspired ADU.
          </p>
          <Button onClick={openCalendlyModal} size="lg" className="mr-4">
            Schedule Consultation
          </Button>
          <Button asChild variant="outline" size="lg" className="text-white border-white hover:bg-white hover:text-black">
            <Link to="/adu-calculator">Get Cost Estimate</Link>
          </Button>
        </section>
      </main>

      <FloatingADUWizard />
      <FloatingReserveButton />
      <Footer />
    </div>
  );
};

export default MediterraneanADUDesignRedwoodCity;